<script>
    import Fa from 'svelte-fa';
    import { faBoxOpen, faTruckFast, faHome, faLocationDot, faCircleCheck } from '@fortawesome/pro-regular-svg-icons';

    import { onMount } from 'svelte';
    import { formatMoney } from '$lib/utils/functions/money.js';
    import { formatDate } from '$lib/utils/functions/date.js';
    import { fade } from 'svelte/transition';

    import Button from '$lib/components/Button.svelte';
    import Picture from '$lib/components/Picture.svelte';
    import GoogleAddressImage from '$lib/components/GoogleAddressImage.svelte';

    export let order;
    export let featureOrder = false;

    let latestTrackingData = new Promise((resolve, reject) => {
        setTimeout(() => {
            reject('Hydration did not mount');
        }, 1000);
    }); // TODO : We can move this to Promise.withResolvers() to reuse the promise @ fetchTrackingData once widely available.
    let trackingNumber = '';
    let fulfillmentStatus = '';
    let latestTrackPoint = '';
    let isPickupOrder = false;
    let pickupOrderCollected = false;

    // Fetch tracking data
    const fetchTrackingData = (trackingNumber) => {
        if (!trackingNumber) return Promise.reject('Undefined Tracking Number');
        return fetch(`/api/customer/tracking?trackingNumber=${trackingNumber}`)
            .then((response) => {
                if (!response.ok) {
                    return Promise.reject('Network response was not ok.');
                }
                try {
                    return response.json();
                } catch (e) {
                    return Promise.reject(e);
                }
            })
            .then((data) => (latestTrackingData = data?.tracking?.[0] || {}));
    };

    // Get order status
    const getFulfillmentStatus = (latest) => {
        if (!latest) return ''; // Catch
        if (latest.includes('allocated')) return 'packed';
        if (latest.includes('delivered')) return 'delivered';
        return 'shipped';
    };

    // Set featured variables
    $: if (featureOrder) {
        // pickup order
        isPickupOrder = order?.shippingLine?.title?.includes('Caktus') ? true : false;
        pickupOrderCollected = isPickupOrder && order?.fulfillments?.edges[0]?.node?.status === 'SUCCESS' ? true : false;

        // shipping order
        trackingNumber = order?.fulfillments?.edges[0]?.node?.trackingInformation?.[0]?.number;
        latestTrackPoint = latestTrackingData?.events?.[0]?.description?.toLowerCase();
        fulfillmentStatus = getFulfillmentStatus(latestTrackPoint);
    }

    onMount(() => {
        if (featureOrder && !isPickupOrder && trackingNumber) latestTrackingData = fetchTrackingData(trackingNumber);
    });
</script>

<a href="/account/orders/{btoa(order?.id)}">
    <div in:fade={{ delay: 0, duration: 250 }}>
        <div class="overflow-hidden rounded-xl border border-base-900/5 bg-base-900 bg-opacity-60 p-4 shadow-sm">
            <div class="flex flex-col items-center gap-4">
                <!-- Order Info -->
                <div class="flex w-full">
                    <div class="basis-1/2">
                        <span class="block font-semibold">{order?.name}</span>
                        <span class="block text-sm">Order placed {formatDate(order?.processedAt, 'long')}</span>
                        <span class="block text-sm">Order Total {formatMoney(order?.totalPrice?.amount)}</span>
                    </div>

                    <div class="flex basis-1/2 items-center justify-end">
                        {#each order?.lineItems.edges as item (item)}
                            {#if item?.node?.image?.url}
                                <Picture
                                    src={item?.node?.image?.url}
                                    alt="Product Image"
                                    width="50"
                                    height="50"
                                    imageSizes={[50, 50, 50]}
                                    lazy={false}
                                    classes="mx-2 inline-block overflow-hidden rounded-full"
                                />
                            {/if}
                        {/each}
                    </div>
                </div>

                <!-- Featured Order -->
                {#if featureOrder}
                    <!-- Google Map -->
                    <GoogleAddressImage
                        address={order?.shippingAddress || {
                            address1: '10B Manchester Street',
                            address2: '',
                            city: 'Paraparaumu',
                            zip: '5032',
                            country: 'New Zealand'
                        }}
                        width="700"
                        height="250"
                        zoom="16"
                        classes="overflow-hidden rounded-xl"
                    />

                    {#if isPickupOrder}
                        <!-- Pickup order -->
                        <div class={`text-center ${pickupOrderCollected ? ' font-semibold text-green-500 dark:text-green-400' : ''}`}>
                            <Fa icon={faLocationDot} class="mr-2 inline-block" size="1x" />
                            {#if !pickupOrderCollected}
                                <span class="inline-block">Pickup Order Confirmed</span>
                                <span class="block text-sm text-color-text/70">We'll email you when its ready to collect!</span>
                            {:else}
                                <span class="inline-block">Collected</span>
                                <span class="block text-sm text-color-text/70">Your order was collected!</span>
                            {/if}
                        </div>
                    {:else}
                        {#if trackingNumber}
                            <!-- Shipped Order -->
                            {#await latestTrackingData}
                                <div class="">
                                    {#if trackingNumber}
                                        <div class="text-center">
                                            <Fa icon={faLocationDot} class="mr-2 inline-block" size="1x" />
                                            <span class="inline-block">Latest Tracking Info</span>
                                            <span class="block text-sm text-color-text/70">Location</span>
                                        </div>
                                    {/if}
                                </div>
                            {:then}
                                <div class="text-center duration-300 ease-in-out">
                                    <div class={`${fulfillmentStatus === 'delivered' ? ' font-semibold text-green-500 dark:text-green-400' : ''}`}>
                                        <div class="capitalize">
                                            {#if fulfillmentStatus === 'delivered'}
                                                <Fa icon={faCircleCheck} class="mr-2 inline-block" size="1x" />
                                            {:else}
                                                <Fa icon={faLocationDot} class="mr-2 inline-block" size="1x" />
                                            {/if}
                                            <span class="inline-block">{latestTrackPoint?.split('-')?.[0] || 'Your parcel is on the way'}</span>
                                            <span class="block text-sm text-color-text/70">
                                                {#if fulfillmentStatus === 'packed'}
                                                    Caktus Warehouse
                                                {:else if !latestTrackPoint?.split('-')?.[1]}
                                                    In Transit
                                                {:else}
                                                    {latestTrackPoint?.split('-')?.[1]}
                                                {/if}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            {:catch}
                                <!-- do nothing... -->
                            {/await}
                        {:else if !trackingNumber && !fulfillmentStatus}
                            <!-- Order Confirmed -->
                            <div class="text-center">
                                <Fa icon={faLocationDot} class="mr-2 inline-block" size="1x" />
                                <span class="inline-block">Your order is confirmed</span>
                                <span class="block text-sm text-color-text/70">Caktus Warehouse</span>
                            </div>
                        {:else if !trackingNumber && fulfillmentStatus === 'shipped'}
                            <!-- Order Shipped - no tracking fallback -->
                            <div class="text-center">
                                <Fa icon={faLocationDot} class="mr-2 inline-block" size="1x" />
                                <span class="inline-block">Your order Shipped</span>
                                <span class="block text-sm text-color-text/70">On the way!</span>
                            </div>
                        {/if}

                        <!-- Stepper progress bar -->
                        <div class="flex w-full items-center">
                            <div
                                class={`flex h-10 w-10 items-center justify-center rounded-full shadow-xl duration-300 ease-in-out
                                ${fulfillmentStatus ? 'bg-color-text text-white dark:bg-purple-300 dark:text-base-900' : 'bg-base-700'}`}
                            >
                                <Fa icon={faBoxOpen} class="block" size="0.8x" />
                            </div>
                            <div
                                class={`flex-auto border-t-4 duration-300 ease-in-out
                            ${
                                fulfillmentStatus === 'shipped' || fulfillmentStatus === 'delivered'
                                    ? 'border-color-text dark:border-purple-300'
                                    : 'border-base-700'
                            }`}
                            ></div>

                            <div
                                class={`flex h-10 w-10 items-center justify-center rounded-full shadow-xl duration-300 ease-in-out
                            ${
                                fulfillmentStatus === 'shipped' || fulfillmentStatus === 'delivered'
                                    ? 'bg-color-text text-white dark:bg-purple-300 dark:text-base-900'
                                    : 'bg-base-700'
                            }`}
                            >
                                <Fa icon={faTruckFast} class="block" size="0.8x" />
                            </div>
                            <div
                                class={`flex-auto border-t-4 duration-300 ease-in-out  ${
                                    fulfillmentStatus === 'delivered' ? 'border-color-text dark:border-purple-300' : 'border-base-700'
                                }`}
                            ></div>

                            <div
                                class={`flex h-10 w-10 items-center justify-center rounded-full shadow-xl duration-300 ease-in-out
                            ${fulfillmentStatus === 'delivered' ? 'bg-color-text text-white dark:bg-purple-300 dark:text-base-900' : 'bg-base-700'}`}
                            >
                                <Fa icon={faHome} class="block" size="0.8x" />
                            </div>
                        </div>

                        <!-- Button placeholder -->
                        {#await latestTrackingData}
                            {#if trackingNumber}
                                <div
                                    class="-mb-4 flex h-9 w-full flex-col justify-center rounded-xl bg-black/80 px-3.5 text-center text-sm font-semibold text-white dark:bg-base-700"
                                >
                                    Loading...
                                </div>
                            {/if}
                        {/await}
                    {/if}
                {/if}

                <!-- Card Buttons -->
                <div class="flex w-full gap-4">
                    {#if (featureOrder && !isPickupOrder && fulfillmentStatus === 'packed') || (isPickupOrder && !pickupOrderCollected)}
                        <Button style="Secondary" size="Small" href="/account/orders/{btoa(order?.id)}">View Order</Button>
                    {:else if featureOrder && fulfillmentStatus === 'shipped'}
                        <Button
                            style="Primary"
                            size="Small"
                            href="/pages/tracking?trackingNumber={order?.fulfillments?.edges[0]?.node?.trackingInformation?.[0]?.number}"
                        >
                            Track Your Order
                        </Button>
                    {:else if (featureOrder && fulfillmentStatus === 'delivered') || !featureOrder || (isPickupOrder && pickupOrderCollected)}
                        <Button style="Secondary" size="Small" href="/account/orders/reorder/{btoa(order?.id)}">Reorder</Button>
                    {/if}
                </div>
            </div>
        </div>
    </div>
</a>
